import React, { useEffect, useRef, useState } from "react";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Upload, FileText, Download, Trash2, Paperclip } from "lucide-react";
import { toast } from "sonner";

const fmtSize = (bytes) => {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} Ko`;
  return `${(kb / 1024).toFixed(2)} Mo`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function DocumentsPanel({ ownerType, ownerId, title = "Pièces jointes" }) {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const fileRef = useRef(null);
  const canEdit = ["admin", "secretaire", "commercial", "comptable"].includes(user?.role);

  const load = async () => {
    try {
      const { data } = await api.get("/documents", { params: { owner_type: ownerType, owner_id: ownerId } });
      setDocs(data);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => { load(); }, [ownerType, ownerId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo)");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("owner_type", ownerType);
      fd.append("owner_id", ownerId);
      fd.append("label", label);
      fd.append("file", file);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté");
      setLabel("");
      e.target.value = "";
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploading(false);
    }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: d.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossible de télécharger");
    }
  };

  const remove = async (d) => {
    if (!confirm(`Supprimer "${d.original_name}" ?`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Document supprimé");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="tpco-card overflow-hidden" data-testid={`documents-panel-${ownerType}`}>
      <div className="px-6 py-4 border-b border-[#E2E5F1] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Paperclip className="w-4 h-4 text-[#1E3A8A]" />
          <h3 className="font-display text-lg font-bold">{title}</h3>
          <span className="text-xs text-[#9CA3B5]">· {docs.length}</span>
        </div>
        {canEdit && (
          <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-semibold cursor-pointer disabled:opacity-60" data-testid="document-upload-btn">
            <Upload className="w-3.5 h-3.5" />
            {uploading ? "Envoi…" : "Téléverser"}
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
              onChange={onFile}
              disabled={uploading}
              data-testid="document-file-input"
            />
          </label>
        )}
      </div>
      {canEdit && (
        <div className="px-6 py-3 bg-[#F8FAFC] border-b border-[#E2E5F1]">
          <input
            type="text"
            placeholder="Libellé du prochain document (ex: CNI recto, Contrat signé)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full text-xs px-3 py-2 rounded-md border border-[#E2E5F1] bg-white focus:outline-none focus:border-[#1E3A8A]"
            data-testid="document-label-input"
          />
          <div className="text-[10px] text-[#9CA3B5] mt-1">PDF, JPG, PNG, DOCX · max 10 Mo</div>
        </div>
      )}
      {docs.length === 0 ? (
        <div className="p-8 text-center text-sm text-[#9CA3B5]">Aucune pièce jointe.</div>
      ) : (
        <ul className="divide-y divide-[#E2E5F1]">
          {docs.map((d) => (
            <li key={d.id} className="px-6 py-3 flex items-center gap-3 hover:bg-[#F8FAFC]" data-testid={`document-${d.id}`}>
              <FileText className="w-5 h-5 text-[#1E3A8A] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[#0F1A3D] truncate">{d.label || d.original_name}</div>
                <div className="text-[11px] text-[#4B5775] truncate">{d.original_name} · {fmtSize(d.size)} · {fmtDate(d.created_at)}{d.uploaded_by ? ` · ${d.uploaded_by}` : ""}</div>
              </div>
              <button
                onClick={() => download(d)}
                className="p-2 rounded-md hover:bg-[#E8EBFA] text-[#1E3A8A]"
                title="Télécharger"
                data-testid={`document-download-${d.id}`}
              >
                <Download className="w-4 h-4" />
              </button>
              {canEdit && (
                <button
                  onClick={() => remove(d)}
                  className="p-2 rounded-md hover:bg-[#FCE4EC] text-[#E91E63]"
                  title="Supprimer"
                  data-testid={`document-delete-${d.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
